"use client";
import React, { useState, useRef } from "react";
import { X, Trash2, FileText } from "lucide-react";
import { LuCloudUpload } from "react-icons/lu";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";


const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function UploadCSVModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const modalRef = useRef(null);
  const [excelData, setExcelData] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Please select a valid CSV or Excel file");
      return;
    }

    setFile(selectedFile);

    if (selectedFile.type !== "text/csv") {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target?.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        setExcelData(jsonData);
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setExcelData([]);
  };

  const truncateFileName = (name, maxLength = 22) => {
    if (!name) return "";
    const ext = name.includes(".xlsx") ? ".xlsx" : ".csv";
    if (name.length <= maxLength) return name;
    const visible = name.replace(ext, "");
    return visible.slice(0, maxLength - ext.length - 3) + "..." + ext;
  };

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const validateInstagramUsers = async (data) => {
    const invalidUsers = data.filter(
      (row) =>
        row["Instagram Username (can be empty)"] &&
        row["Instagram Username (can be empty)"].trim() === ""
    );
    return invalidUsers.length === 0;
  };

  const handleUploadFile = async () => {
    if (!file) {
      toast.error("Please select a file first!");
      return;
    }

    if (excelData.length > 0) {
      const valid = await validateInstagramUsers(excelData);
      if (!valid) {
        toast.error(
          "File contains invalid Instagram usernames. Please correct them before uploading."
        );
        return;
      }
    }

    setUploading(true);
    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error("No access token found!");
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", file, file.name);

      const headers = buildHeaders(token, { 
        "Content-Type": undefined 
      });

      const response = await fetch(
        "https://api.backend.halper.ai/api/client/upload-clients-excel/",
        {
          method: "POST",
          headers,
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Upload failed");
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", "new_client_added");
      }

      toast.success("Clients uploaded successfully!");
      setFile(null);
      setExcelData([]);
      onClose();

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error("Upload error: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = [
      {
        "Full Name": "",
        "Instagram Username (can be empty)": "",
        "Email (must be a valid email or empty)": "",
        "Phone Number (ex: +12125551234 or empty)": "",
        "Birthday (can be empty, ex: DD/MM/YYYY)": "",
        "Country (can be empty)": "",
        "Family Status (can be empty)": "",
        "Hobbies (can be empty)": "",
        "About Service Notes (can be empty)": "",
        "Other Notes (can be empty)": "",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "clients_template.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);

    toast.success("📄 Template downloaded");
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm z-50"
        onClick={handleClickOutside}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.9, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 30 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="relative flex flex-col items-center bg-white border border-[#9A99AB33] rounded-2xl shadow-lg w-[382px] px-6 py-6 pb-8"
        >
          <motion.button
            whileHover={{ rotate: 90 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="absolute top-4 right-4 text-[#302B2B] hover:text-gray-500 transition"
          >
            <X size={22} strokeWidth={2} />
          </motion.button>

          <div className="flex flex-col items-center justify-center border-2 border-dashed border-[#5563F6] rounded-lg w-[322px] mt-4 p-8 gap-6">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="w-[92px] h-[92px] bg-[#5563F61A] rounded-full flex items-center justify-center"
            >
              <LuCloudUpload
                className="w-[60px] h-[60px] text-[#5563F6]"
                strokeWidth={2.5}
              />
            </motion.div>

            <AnimatePresence mode="wait">
              {!file ? (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col items-center gap-3"
                >
                  <h3 className="text-[20px] font-bold text-[#302B2B] text-center">
                    Select CSV or Excel File to upload
                  </h3>
                  <label className="border border-[#5563F6] text-[#5563F6] px-5 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#F6F8FF] transition">
                    Select file
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="text-xs text-[#9A99AB] mt-1">
                    Maximum file size 10mb
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleDownloadTemplate}
                    className="bg-blue-500 text-white rounded-xl w-[290px] py-3 font-medium hover:bg-blue-600 transition"
                  >
                    Download Template
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col items-center gap-4"
                >
                  <h3 className="text-[20px] font-bold text-[#302B2B] text-center">
                    You have selected a file
                  </h3>

                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-between border border-[#302B2B] rounded-xl px-4 py-3 w-[290px]"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileText
                        size={20}
                        className="text-[#302B2B] flex-shrink-0"
                      />
                      <span
                        className="text-[14px] text-[#302B2B] truncate max-w-[190px]"
                        title={file.name}
                      >
                        {truncateFileName(file.name)}
                      </span>
                    </div>
                    <button onClick={handleRemoveFile}>
                      <Trash2 size={18} className="text-[#302B2B]" />
                    </button>
                  </motion.div>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleUploadFile}
                    disabled={uploading}
                    className="bg-green-500 text-white rounded-xl w-[290px] py-3 font-medium hover:bg-green-600 transition disabled:opacity-50"
                  >
                    {uploading ? "Uploading..." : "Upload File"}
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}